import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { insertLenderCommissionSchema, type LenderCommission } from "@shared/schema";
import { z } from "zod";

type CommissionFormData = z.infer<typeof insertLenderCommissionSchema>;

interface CommissionFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lenderProductId: string;
  commission?: LenderCommission;
  onCommissionCreated?: (commission: LenderCommission) => void;
}

export function CommissionFormModal({
  open,
  onOpenChange,
  lenderProductId,
  commission,
  onCommissionCreated,
}: CommissionFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!commission;

  const defaultValues: Partial<CommissionFormData> = {
    lenderProductId,
    dealType: "",
    upfrontRate: "0.0000",
    trailRate: "0.0000",
    effectiveFrom: "",
    effectiveTo: "",
    notes: "",
  };

  // Helper: Convert decimal to percentage for display (0.025 -> 2.5)
  const decimalToPercent = (value: string | null | undefined): string => {
    if (!value || value === "") return "";
    const decimal = parseFloat(value);
    if (isNaN(decimal)) return "";
    return (decimal * 100).toString();
  };

  // Helper: Convert percentage to decimal for storage (2.5 -> 0.025)
  const percentToDecimal = (value: string | null | undefined): string => {
    if (!value || value === "") return "0.0000";
    const percent = parseFloat(value);
    if (isNaN(percent)) return "0.0000";
    return (percent / 100).toFixed(4);
  };

  const form = useForm<CommissionFormData>({
    resolver: zodResolver(insertLenderCommissionSchema),
    defaultValues,
    mode: "onChange",
    criteriaMode: "all",
  });

  useEffect(() => {
    if (open) {
      if (commission) {
        // Only reset with form-relevant fields to avoid Zod strict mode validation errors
        // Database-only fields (id, createdAt, updatedAt, organisationId) cause silent validation failures
        form.reset({
          lenderProductId: commission.lenderProductId,
          dealType: commission.dealType,
          upfrontRate: decimalToPercent(commission.upfrontRate), // Convert for display
          trailRate: decimalToPercent(commission.trailRate ?? "0.0000"), // Convert for display
          effectiveFrom: commission.effectiveFrom,
          effectiveTo: commission.effectiveTo ?? "",
          notes: commission.notes ?? "",
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, commission, form]);

  const createMutation = useMutation({
    mutationFn: async (data: CommissionFormData) => {
      const dataWithDecimals = {
        ...data,
        upfrontRate: percentToDecimal(data.upfrontRate), // Convert to decimal for storage
        trailRate: percentToDecimal(data.trailRate), // Convert to decimal for storage
      };
      const response = await apiRequest("POST", "/api/crm/lender-commissions", dataWithDecimals);
      return response.json();
    },
    onSuccess: async (newCommission: LenderCommission) => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/lender-commissions"] });
      queryClient.refetchQueries({ queryKey: ["/api/crm/lender-products", lenderProductId, "commissions"] });
      toast({
        title: "Commission created",
        description: "The commission has been created successfully.",
      });
      onOpenChange(false);
      if (onCommissionCreated) onCommissionCreated(newCommission);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CommissionFormData) => {
      const dataWithDecimals = {
        ...data,
        upfrontRate: percentToDecimal(data.upfrontRate), // Convert to decimal for storage
        trailRate: percentToDecimal(data.trailRate), // Convert to decimal for storage
      };
      return apiRequest("PATCH", `/api/crm/lender-commissions/${commission?.id}`, dataWithDecimals);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/lender-commissions"] });
      toast({
        title: "Commission updated",
        description: "The commission has been updated successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: CommissionFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Commission" : "Add Commission"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update commission information" : "Create a new commission rate"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="dealType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Deal Type *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Commercial Property"
                      data-testid="input-deal-type"
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Type of deal this commission applies to
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="upfrontRate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Upfront Rate *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        step="0.01"
                        placeholder="2.50"
                        data-testid="input-upfront-rate"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Enter as percentage (e.g., 2.5 for 2.5%)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="trailRate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trail Rate</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        type="number"
                        step="0.01"
                        placeholder="0.10"
                        data-testid="input-trail-rate"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Enter as percentage (e.g., 0.1 for 0.1%)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="effectiveFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective From *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="date"
                        data-testid="input-effective-from"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="effectiveTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective To</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value || ""}
                        type="date"
                        data-testid="input-effective-to"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ""}
                      placeholder="Additional notes..."
                      data-testid="input-notes"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending} data-testid="button-save">
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? "Update" : "Create"} Commission
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
